import React, { useEffect, useState } from "react";
import {
  InterviewSchedule,
  SelectedCandidate,
  RejectedCandidates,
  BackOutCandidate,
  viewResumeUrl,
  SystemRemark,
} from "../../CandidateServices/CandidateService";
import { GetAssessmentCandidates } from "../Services/AssessmentCandidatesService";
import { Funnel, MoreVertical, FileText } from "lucide-react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import ColumnFilterModal from "../../../CommonModals/ColumnFilterModal";
import Pagination from "../../../CommonModals/Pagination";
import CandidateDetailsModal from "../../NewApplications/Modal/CandidateDetailsModal";
import ViewResumeModal from "../../../Jobs/SendCandidates/Modals/ViewResumeModal";
import NormalAlert from "../../../../Alerts/NormalAlert";

const AssessmentCandidates = () => {
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);

  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [remark, setRemark] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeUrl, setResumeUrl] = useState("");

  const [alert, setAlert] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [openMenu, setOpenMenu] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [activeFilter, setActiveFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");
  const [filters, setFilters] = useState({
    jobTitel: [],
    submitByType: [],
    functionName: [],
    department: [],
    grade: [],
  });

  const filterOptions = {
    jobTitel: [...new Set(candidates.map((c) => c.jobTitel))],
    submitByType: [...new Set(candidates.map((c) => c.submitByType))],
    functionName: [...new Set(candidates.map((c) => c.functionName))],
    department: [...new Set(candidates.map((c) => c.department))],
    grade: [...new Set(candidates.map((c) => c.grade))],
  };

  const filteredCandidates = candidates.filter((candidate) => {
    const matchesJobTitle =
      (filters.jobTitle || []).length === 0 ||
      (filters.jobTitle || []).includes(candidate.jobTitel);

    const matchesSubmitType =
      (filters.submitByType || []).length === 0 ||
      (filters.submitByType || []).includes(candidate.submitByType);

    const matchesFunction =
      (filters.functionName || []).length === 0 ||
      (filters.functionName || []).includes(candidate.functionName);

    const matchesDepartment =
      (filters.department || []).length === 0 ||
      (filters.department || []).includes(candidate.department);

    const matchesGrade =
      (filters.grade || []).length === 0 ||
      (filters.grade || []).includes(candidate.grade);

    const matchesSearch = [
      candidate.jobTitel,
      candidate.submitByType,
      candidate.functionName,
      candidate.department,
      candidate.grade,
      candidate.name,
      candidate.email,
    ].some((field) => field?.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
      matchesJobTitle &&
      matchesSubmitType &&
      matchesFunction &&
      matchesDepartment &&
      matchesGrade &&
      matchesSearch
    );
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await GetAssessmentCandidates();
        const data = res?.data;

        if (!data) throw new Error();

        // Always set candidates
        setCandidates(data.assessmentCandidates || []);

        // Show alert ONLY if it's not "No candidate found."
        if (data.status === "Fail" && data.message !== "No candidate found.") {
          setAlert({
            type: "fail",
            message: data.message || "Something went wrong",
          });
        }
      } catch (err) {
        const backendMessage = err?.response?.data?.message;

        if (backendMessage !== "No candidate found.") {
          setAlert({
            type: "fail",
            message: backendMessage || "Failed to load assessment candidates",
          });
        }

        setCandidates([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentCandidates = filteredCandidates.slice(indexOfFirst, indexOfLast);

  const handleResumeClick = async (candidate) => {
    if (!candidate?.resumePath) {
      setAlert({ type: "fail", message: "Resume not available" });
      return;
    }

    const fileName = candidate.resumePath.split("/").pop();
    const url = await viewResumeUrl(fileName);

    setResumeUrl(url);
    setShowModal(false);
    setShowResumeModal(true);
  };

  const handleAction = async () => {
    try {
      let res;

      if (modalType === "Select") {
        res = await SelectedCandidate(
          selectedCandidate.submitedCandidateUID,
          5,
          remark,
        );
      }

      if (modalType === "Rejected") {
        res = await RejectedCandidates(
          selectedCandidate.submitedCandidateUID,
          7,
          remark,
        );
      }

      if (modalType === "Backout") {
        res = await BackOutCandidate(
          selectedCandidate.submitedCandidateUID,
          10,
          remark,
        );
      }

      if (res?.data) {
        setAlert({
          type: res.data.status,
          message: res.data.message,
        });

        setShowModal(false);
        setModalType(null);
        setRemark("");
      }
    } catch (err) {
      setAlert({ type: "fail", message: "Action failed" });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";

    try {
      return dateStr.split("T")[0];
    } catch {
      return "";
    }
  };

  return (
    <>
      <div className="w-full py-8 mt-12">
        <div className="max-w-[1240px] mx-auto">
          <div className="bg-white rounded-2xl px-6 py-6 shadow-sm">
            {/* HEADER */}
            <div className="bg-master-header text-white rounded-xl px-3 py-2 mb-6">
              <h3 className="font-bold text-lg">Assessment Candidates</h3>
            </div>

            {/* SEARCH */}
            <div className="mb-6">
              <input
                type="text"
                placeholder="Search by name, title etc"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border border-gray-300 rounded-full px-5 py-2 w-full max-w-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* TABLE */}
            <div className="border rounded-lg overflow-visible">
              <table className="w-full">
                <thead className="bg-[#f5f8ff] text-sm uppercase">
                  <tr>
                    <th className="py-3 text-left w-[5%]">#</th>

                    <th className="py-3 text-left w-[20%]">Candidate Info</th>

                    <th className="py-3 text-left w-[15%]">
                      <div className="flex items-center gap-2">
                        Submitted By
                        <Funnel
                          size={14}
                          onClick={() => setActiveFilter("submitByType")}
                          className="cursor-pointer text-primary"
                        />
                      </div>
                    </th>

                    <th className="py-3 text-left w-[20%]">
                      <div className="flex items-center gap-2">
                        Role Info
                        <Funnel
                          size={14}
                          onClick={() => setActiveFilter("jobTitel")}
                          className="cursor-pointer text-primary"
                        />
                      </div>
                    </th>

                    <th className="py-3 text-left w-[15%]">
                      <div className="flex items-center gap-2">
                        Function
                        <Funnel
                          size={14}
                          onClick={() => setActiveFilter("functionName")}
                          className="cursor-pointer text-primary"
                        />
                      </div>
                    </th>
                    <th className="py-3 text-left w-[15%]">
                      <div className="flex items-center gap-2">
                        Department
                        <Funnel
                          size={14}
                          onClick={() => setActiveFilter("department")}
                          className="cursor-pointer text-primary"
                        />
                      </div>
                    </th>
                    <th className="py-3 text-left w-[20%]">
                      <div className="flex items-center gap-2">
                        Grade
                        <Funnel
                          size={14}
                          onClick={() => setActiveFilter("grade")}
                          className="cursor-pointer text-primary"
                        />
                      </div>
                    </th>

                    <th className="py-3 text-center w-[10%]">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <SkeletonTheme>
                      {[...Array(5)].map((_, idx) => (
                        <tr key={idx} className="border-t">
                          {[...Array(7)].map((__, i) => (
                            <td key={i} className="py-4 px-3">
                              <Skeleton height={20} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </SkeletonTheme>
                  ) : currentCandidates.length ? (
                    currentCandidates.map((candidate, idx) => (
                      <tr key={idx} className="border-t hover:bg-gray-50">
                        <td className="py-6">{indexOfFirst + idx + 1}</td>
                        {/* <td>{candidate.roleCode || "--"}</td> */}
                        <td className="cursor-pointer text-left">
                          <div className="flex flex-col items-start">
                            <span
                              className="text-blue-500 font-semibold"
                              onClick={() => {
                                setSelectedCandidate(candidate);
                                setShowModal(true);
                              }}
                            >
                              {candidate.name || "-"}
                            </span>

                            <span className="text-gray-600 text-sm">
                              {candidate.mobile || "-"}
                            </span>

                            <span className="text-gray-500 text-xs">
                              {candidate.email || "-"}
                            </span>
                          </div>
                        </td>

                        <td className="text-left">
                          <div className="flex flex-col items-start">
                            <span className="text-sm font-semibold">
                              {candidate.submitedBy}
                            </span>

                            <span className="text-gray-500 text-sm">
                              {candidate.submitByType}
                            </span>

                            <span className="text-gray-500 text-sm">
                              {formatDate(candidate.submitDate)}
                            </span>
                          </div>
                        </td>

                        <td className="text-left">
                          <div className="flex flex-col items-start">
                            <span className="text-sm font-semibold">
                              {candidate.roleCode || "-"}
                            </span>

                            <span className="text-gray-500 text-sm">
                              {candidate.jobTitel || "-"}
                            </span>
                          </div>
                        </td>
                        <td>{candidate.functionName}</td>
                        <td>{candidate.department}</td>
                        <td>{candidate.grade}</td>
                        <td>
                          <div className="flex justify-center items-center gap-3 relative">
                            <button
                              onClick={() => handleResumeClick(candidate)}
                              className="p-1 hover:bg-gray-100 rounded-full"
                            >
                              <FileText size={18} className="text-primary" />
                            </button>

                            <div className="relative">
                              <button
                                onClick={() =>
                                  setOpenMenu(
                                    openMenu === candidate.submitedCandidateUID
                                      ? null
                                      : candidate.submitedCandidateUID,
                                  )
                                }
                                className="p-1 hover:bg-gray-100 rounded-full"
                              >
                                <MoreVertical size={18} />
                              </button>

                              {openMenu === candidate.submitedCandidateUID && (
                                <div className="absolute right-0 mt-2 w-40 bg-white border rounded-lg shadow-lg z-50">
                                  <button
                                    onClick={() => {
                                      setSelectedCandidate(candidate);
                                      setModalType("Selected");
                                      setShowModal(true);
                                      setOpenMenu(null);
                                    }}
                                    className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                  >
                                    Select
                                  </button>

                                  <button
                                    onClick={() => {
                                      setSelectedCandidate(candidate);
                                      setModalType("Rejected");
                                      setShowModal(true);
                                      setOpenMenu(null);
                                    }}
                                    className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                  >
                                    Reject
                                  </button>

                                  <button
                                    onClick={() => {
                                      setSelectedCandidate(candidate);
                                      setModalType("Backout");
                                      setShowModal(true);
                                      setOpenMenu(null);
                                    }}
                                    className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                  >
                                    Back-out
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="py-6 text-red-500 text-center">
                        No Candidate Found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalItems={filteredCandidates.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              setItemsPerPage={setItemsPerPage}
            />
          </div>
        </div>
      </div>

      {/* Modals */}

      <CandidateDetailsModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setModalType(null);
        }}
        selectedCandidate={selectedCandidate}
        modalActionType={modalType}
        remark={remark}
        setRemark={setRemark}
        onActionSubmit={handleAction}
        showSystemRemarkButton={true}
        showResumeButton={true}
        showInterviewSection={false}
        onViewResume={() => handleResumeClick(selectedCandidate)}
      />

      <ColumnFilterModal
        show={!!activeFilter}
        onHide={() => setActiveFilter(null)}
        title={activeFilter}
        items={filterOptions[activeFilter] || []}
        filteredItems={(filterOptions[activeFilter] || []).filter((item) =>
          item?.toLowerCase().includes(filterSearch.toLowerCase()),
        )}
        selectedItems={filters[activeFilter] || []}
        setSelectedItems={(values) =>
          setFilters((prev) => ({
            ...prev,
            [activeFilter]: values,
          }))
        }
        searchText={filterSearch}
        setSearchText={setFilterSearch}
      />

      <ViewResumeModal
        show={showResumeModal}
        onClose={() => {
          setShowResumeModal(false);
          setShowModal(true);
        }}
        resumeUrl={resumeUrl}
      />

      {alert && (
        <NormalAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default AssessmentCandidates;
